import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Crosshair, Dice5, Undo2, RotateCcw, Check, ChevronRight, Target,
  Trophy, Save, Flag, ArrowRight, FileText, Users, Repeat, StickyNote, CheckCircle2,
} from "lucide-react";
import { cellValue, cellLabel, fmtScore, effectiveCell, factionIcon, valueStyle, tierStyle, objWinDelta } from "@/lib/game";
import { api } from "@/lib/api";
import ResolverMatrix from "@/components/ResolverMatrix";
import ListDialog from "@/components/ListDialog";
import OpponentPicker from "@/components/OpponentPicker";
import PairingSequence from "@/components/PairingSequence";
import WinRoutes from "@/components/WinRoutes";
import BreakdownLabel from "@/components/BreakdownLabel";
import { useTeam } from "@/lib/team";

const OUTCOME_STYLE = {
  win: "text-emerald-400",
  loss: "text-red-400",
  tossup: "text-slate-400",
};

// Record colour: green when the round is won (>=3 wins), red when lost (>=3 losses).
const recordClass = (wins, losses) =>
  wins >= 3 ? "text-emerald-400" : losses >= 3 ? "text-red-400" : "text-slate-300";

export default function ResolverTab({ setup, updateSetup, replaceSetup }) {
  const { activeId } = useTeam();
  const idsA = useMemo(() => setup.players.filter((p) => p.team === "A").map((p) => p.id), [setup]);
  const idsB = useMemo(() => setup.players.filter((p) => p.team === "B").map((p) => p.id), [setup]);
  const valid = idsA.length === idsB.length && idsA.length > 0;

  const nameMap = useMemo(() => {
    const m = {};
    setup.players.forEach((p) => (m[p.id] = p));
    return m;
  }, [setup]);
  const nameOf = (id) => nameMap[id] || { name: id, faction: "" };

  const initialState = useMemo(
    () => ({ ra: idsA, rb: idsB, pending: null, pteam: null, offered: [], pairings: [] }),
    [idsA, idsB]
  );

  const [analysis, setAnalysis] = useState(null);
  const [err, setErr] = useState("");

  // ---- shared, persisted pairing session (lives in setup.resolver) ----
  // Moving the session into setup.resolver makes it survive tab switches / app close
  // and sync live across every teammate's device over the team WebSocket.
  const resolver = setup.resolver || null;

  // A stored session is valid only if every player id it references still exists in the
  // current roster. If the opponent/roster changed, discard it (back to the roll-off screen).
  const rosterIds = useMemo(() => new Set([...idsA, ...idsB]), [idsA, idsB]);
  const resolverValid = useMemo(() => {
    const st = resolver?.state;
    if (!st) return false;
    const refs = [
      ...(st.ra || []), ...(st.rb || []), ...(st.offered || []),
      ...(st.pairings || []).flatMap((p) => [p.a, p.b]),
      ...(st.pending ? [st.pending] : []),
    ];
    return refs.every((id) => rosterIds.has(id));
  }, [resolver, rosterIds]);

  const started = !!resolver?.started && resolverValid;
  const stack = resolver?.stack || [];
  const tables = resolver?.tables || {};
  const saved = !!resolver?.saved;
  const rollWinner = resolver?.rollWinner || "us";
  const sessionState = started && resolver?.state ? resolver.state : initialState;

  // Merge a partial change into the shared resolver and broadcast/persist it.
  const patchResolver = (partial) =>
    updateSetup({ ...setup, resolver: { ...(setup.resolver || {}), ...partial } });

  const runAnalysis = useCallback(async (st, youAre) => {
    try {
      const r = await api.teamAnalyze(activeId, st, youAre);
      setAnalysis(r);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Analysis failed");
    }
  }, [activeId]);

  // Re-analyze whenever the engine state OR the A/B election changes — from a local
  // action or a teammate's action arriving over the WebSocket. Passing you_are makes the
  // backend consistent even before the (debounced) setup patch has persisted.
  const stateKey = JSON.stringify(sessionState);
  useEffect(() => {
    if (valid) runAnalysis(sessionState, setup.you_are);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey, valid, runAnalysis, setup.you_are]);

  const teamName = (t) => (t === "A" ? setup.team_a_name : setup.team_b_name);
  const setRollWinner = (v) => patchResolver({ rollWinner: v });
  const startPairing = () => patchResolver({ started: true, state: initialState, stack: [], saved: false });
  const pick = (opt) => patchResolver({ state: opt.next_state, stack: [...stack, sessionState] });
  const undo = () => {
    if (!stack.length) return;
    patchResolver({ state: stack[stack.length - 1], stack: stack.slice(0, -1) });
  };
  const restart = () => patchResolver({ started: false, state: null, stack: [], tables: {}, saved: false });
  const setTable = (key, value) => patchResolver({ tables: { ...tables, [key]: value } });

  const nextRound = () => {
    if (setup.opponent_locked) return toast.error("Opponent is locked — unlock it first to start a new round");
    const n = (setup.round_number || 1) + 1;
    // keep YOUR team, drop the opponent so you can pick the next one
    const players = setup.players.filter((p) => p.team === setup.you_are);
    updateSetup({
      ...setup,
      players,
      round_number: n,
      round_name: `Round ${n}`,
      team_b_name: setup.you_are === "A" ? "Opponent" : setup.team_b_name,
      team_a_name: setup.you_are === "B" ? "Opponent" : setup.team_a_name,
      matrix: {},
      resolver: { started: false, state: null, stack: [], tables: {}, rollWinner: "us", saved: false },
    });
    toast.success(`Round ${n} — pick your next opponent`);
  };

  if (!valid) {
    return (
      <div className="space-y-6">
        <RoundHeader setup={setup} onNextRound={nextRound} />
        <OpponentPicker setup={setup} updateSetup={updateSetup} />
        <ReadinessPanel setup={setup} />
        <Card className="bg-[#121824]/70 border-slate-800 p-8 text-center">
          <Crosshair className="h-8 w-8 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-semibold">Pick your opponent above to begin.</p>
          <p className="text-slate-500 text-sm mt-1">
            Your team: {idsA.length || idsB.length} players · opponent: {setup.players.filter((p) => p.team !== setup.you_are).length}. Both sides need the same number of players.
          </p>
        </Card>
      </div>
    );
  }

  // ---- pre-start ----
  if (!started) {
    const side = analysis?.side;
    const proj = analysis?.projection;
    const ourRole = setup.you_are;
    const ourName = ourRole === "A" ? setup.team_a_name : setup.team_b_name;
    const oppName = ourRole === "A" ? setup.team_b_name : setup.team_a_name;
    const firstForward = ourRole === "B" ? "us" : "them";
    const recommendedFirst = side ? (side.recommend === "B" ? "us" : "them") : null;

    // Ensure OUR players sit in the chosen procedural slot (A/B) without
    // disturbing matrix/ratings (which are keyed by player id).
    const setOurRole = (role) => {
      if (role === setup.you_are) return;
      const players = setup.players.map((p) => ({ ...p, team: p.team === "A" ? "B" : "A" }));
      updateSetup({
        ...setup, players,
        team_a_name: setup.team_b_name, team_b_name: setup.team_a_name,
        you_are: role,
      });
    };
    const setFirstForward = (who) => setOurRole(who === "us" ? "B" : "A");

    return (
      <div className="space-y-6">
        <RoundHeader setup={setup} onNextRound={nextRound} />
        <OpponentPicker setup={setup} updateSetup={updateSetup} />
        <ReadinessPanel setup={setup} />
        <Card className="bg-[#121824]/70 border-amber-600/30 p-6 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 opacity-10"><Dice5 className="h-48 w-48" /></div>
          <div className="relative">
            <p className="text-xs text-amber-400/80 font-mono uppercase tracking-widest mb-1">Step 1 · Roll-off &amp; Side</p>
            <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-3">Set the Roll-off</h2>
            <p className="text-sm text-slate-400 max-w-2xl mb-5">
              The roll-off winner elects a side. The team that <b>puts a player forward first</b> plays as
              Team B (nominates first, picks 2 matchups + 3 tables); Team A picks 3 matchups + 2 tables.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6 max-w-2xl">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1.5">Who won the roll-off?</p>
                <Segmented value={rollWinner} onChange={setRollWinner} testid="rolloff-winner"
                  options={[{ v: "us", label: ourName || "Us" }, { v: "them", label: oppName || "Them" }]} />
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1.5">Who puts a player forward first? <span className="text-slate-600">(Team B)</span></p>
                <Segmented value={firstForward} onChange={setFirstForward} testid="first-forward"
                  options={[{ v: "us", label: ourName || "Us" }, { v: "them", label: oppName || "Them" }]} />
              </div>
            </div>

            {side && (
              <div className="rounded-lg border border-cyan-700/40 bg-cyan-950/20 px-4 py-3 mb-5 flex items-start gap-3" data-testid="rolloff-recommendation">
                <Dice5 className="h-5 w-5 text-cyan-400 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-slate-200">
                    Best side for you: <b className="text-cyan-300">Team {side.recommend}</b> —{" "}
                    {side.recommend === "B" ? "put a player forward first." : "let them put forward first."}
                  </p>
                  <p className="text-[12px] text-slate-500 mt-0.5">
                    {rollWinner === "us"
                      ? recommendedFirst === firstForward
                        ? "You won the roll — this matches the recommended election. ✓"
                        : "You won the roll — consider switching to the recommended election above."
                      : "They won the roll and will likely elect the side that's best for them."}
                  </p>
                </div>
              </div>
            )}

            {side && (
              <div className="flex flex-wrap gap-4 items-stretch">
                <div className={`rounded-xl border px-5 py-4 ${ourRole === "A" ? "border-amber-500 bg-amber-950/20" : "border-slate-700 bg-slate-900/40"}`}>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">You as Team A</p>
                  <p className={`font-mono text-2xl font-bold ${recordClass(side.wins_as_a, side.losses_as_a)}`} data-testid="side-a-record">{side.wins_as_a}–{side.losses_as_a}</p>
                  <p className="text-[11px] font-mono"><BreakdownLabel breakdown={side.breakdown_as_a} /></p>
                </div>
                <div className={`rounded-xl border px-5 py-4 ${ourRole === "B" ? "border-amber-500 bg-amber-950/20" : "border-slate-700 bg-slate-900/40"}`}>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">You as Team B</p>
                  <p className={`font-mono text-2xl font-bold ${recordClass(side.wins_as_b, side.losses_as_b)}`} data-testid="side-b-record">{side.wins_as_b}–{side.losses_as_b}</p>
                  <p className="text-[11px] font-mono"><BreakdownLabel breakdown={side.breakdown_as_b} /></p>
                </div>
                <div className="rounded-xl border border-slate-700 bg-slate-900/40 px-5 py-4 flex flex-col justify-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">This round you play as</p>
                  <p className="font-display text-2xl font-black text-amber-400">Team {ourRole}</p>
                  <p className="text-[11px] text-slate-500">{firstForward === "us" ? "you nominate first" : "opponent nominates first"}</p>
                </div>
              </div>
            )}

            <Button data-testid="resolver-start-button" onClick={startPairing}
              className="mt-6 bg-amber-500 text-slate-900 hover:bg-amber-400 font-bold text-base px-6 py-5">
              <Crosshair className="h-5 w-5 mr-2" /> Start Pairing Process
            </Button>
          </div>
        </Card>

        {proj && (
          <Card className="bg-[#121824]/70 border-slate-800 p-5">
            <h3 className="font-display font-bold uppercase tracking-wide text-sm mb-3 flex items-center gap-2 flex-wrap">
              <Target className="h-4 w-4 text-cyan-400" /> Optimal projected outcome
              <span className={`font-mono ml-2 ${recordClass(proj.wins, proj.losses)}`} data-testid="proj-record">{proj.wins}–{proj.losses}</span>
              <span className="ml-1 text-xs" data-testid="proj-breakdown"><BreakdownLabel breakdown={proj.breakdown} /></span>
            </h3>
            <ProjectionList proj={proj} setup={setup} lockedCount={0} nameOf={nameOf} />
            {analysis?.sequence?.length > 1 && (
              <div className="mt-4 pt-4 border-t border-slate-800">
                <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Projected pairing sequence — who gets put up against who</p>
                <PairingSequence sequence={analysis.sequence} youAre={setup.you_are} teamAName={setup.team_a_name} teamBName={setup.team_b_name} />
              </div>
            )}
          </Card>
        )}

        {analysis && <WinRoutes analysis={analysis} setup={setup} />}
      </div>
    );
  }

  // ---- stepping ----
  const a = analysis;
  const lockedCount = a?.pairings?.length || 0;
  const total = idsA.length;
  const pendingPlayer = sessionState.pending ? nameOf(sessionState.pending) : null;
  const deciderIsYou = a?.decider_is_you;
  const deciderName = a?.decider ? teamName(a.decider) : null;

  const instruction = () => {
    if (!a) return "";
    if (a.phase === "first_pick")
      return deciderIsYou
        ? `You (${deciderName}) put forward your first player.`
        : `${deciderName} (Team ${a.decider}) nominates first — select who they put forward.`;
    if (a.phase === "nominate")
      return deciderIsYou
        ? `You (${deciderName}) offer two players to face ${pendingPlayer?.name}.`
        : `${deciderName} offers two players to face ${pendingPlayer?.name} — select the pair they offered.`;
    if (a.phase === "choose")
      return deciderIsYou
        ? `You (${deciderName}) choose who faces ${pendingPlayer?.name}. The other stays on the table.`
        : `${deciderName} chooses who faces ${pendingPlayer?.name} — select their pick.`;
    if (a.phase === "forced") return "Only one player remains on each side — the final matchup is forced.";
    return "";
  };

  const saveToHistory = async () => {
    const pairings = a.pairings.map((p) => {
      const yourId = setup.you_are === "A" ? p.a : p.b;
      const oppId = setup.you_are === "A" ? p.b : p.a;
      const oppFaction = nameOf(oppId).faction;
      const cell = effectiveCell(setup, yourId, oppId, oppFaction);
      return {
        a_id: p.a, b_id: p.b,
        a_name: p.a_player.name, a_faction: p.a_player.faction,
        b_name: p.b_player.name, b_faction: p.b_player.faction,
        table: tables[`${p.a}|${p.b}`] || "",
        predicted_tier: cell?.tier || "",
        predicted_value: cellValue(cell),
        actual: "",
      };
    });
    await api.teamSaveHistory(activeId, {
      event_name: setup.event_name, round_name: setup.round_name || `Round ${setup.round_number || 1}`,
      round_number: setup.round_number || 1,
      team_a_name: setup.team_a_name, team_b_name: setup.team_b_name,
      you_are: setup.you_are, predicted_score: a.projection.score, pairings,
      sequence: a.sequence || [], wins: a.projection.wins, losses: a.projection.losses,
      breakdown: a.projection.breakdown || {},
    });
    patchResolver({ saved: true });
    toast.success("Round saved to history");
  };

  return (
    <div className="space-y-5">
      {/* progress */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {Array.from({ length: total }).map((_, i) => (
            <div key={i} className={`h-2 rounded-full transition-all ${i < lockedCount ? "w-10 bg-amber-400" : "w-6 bg-slate-700"}`} />
          ))}
          <span className="text-xs font-mono text-slate-500 ml-2">{lockedCount}/{total} locked</span>
        </div>
        <div className="flex gap-2">
          <Button onClick={undo} disabled={!stack.length} variant="outline"
            className="border-slate-700 bg-transparent text-slate-300 h-9" data-testid="resolver-undo-button">
            <Undo2 className="h-4 w-4 mr-1" /> Undo
          </Button>
          <Button onClick={restart} variant="ghost" className="text-slate-400 hover:text-red-400 h-9" data-testid="resolver-restart-button">
            <RotateCcw className="h-4 w-4 mr-1" /> Restart
          </Button>
          <Button onClick={nextRound} variant="outline" className="border-cyan-700/50 bg-cyan-950/20 text-cyan-300 hover:bg-cyan-900/40 h-9" data-testid="resolver-next-round-button">
            <Repeat className="h-4 w-4 mr-1" /> Next round
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* decision panel */}
        <div className="lg:col-span-3 space-y-4">
          {err && <Card className="bg-red-950/30 border-red-800 p-4 text-red-300 text-sm">{err}</Card>}

          {a?.complete ? (
            <Card className="bg-[#121824]/70 border-emerald-700/40 p-6">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="h-5 w-5 text-amber-400" />
                <h2 className="font-display text-xl font-black uppercase tracking-tight">Pairings Locked</h2>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                All {total} matchups set. Projected result{" "}
                <span className="text-emerald-400 font-mono">{a.projection.wins}–{a.projection.losses}</span>{" "}
                (<span className={a.projection.score >= 0 ? "text-emerald-400" : "text-red-400"}>{fmtScore(a.projection.score)}</span>).
              </p>
              <Button data-testid="save-pairing-history-button" onClick={saveToHistory} disabled={saved}
                className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-bold">
                <Save className="h-4 w-4 mr-2" /> {saved ? "Round saved" : "Save round"}
              </Button>
              <Button data-testid="complete-next-round-button" onClick={nextRound} variant="outline"
                className="ml-2 border-cyan-700/50 bg-cyan-950/20 text-cyan-300 hover:bg-cyan-900/40 font-bold">
                <Repeat className="h-4 w-4 mr-2" /> Next round
              </Button>
            </Card>
          ) : (
            <Card className="bg-[#121824]/70 border-slate-800 p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-mono uppercase tracking-widest text-amber-400/80">
                  {a?.phase === "forced" ? "Final matchup" : `Step · ${a?.phase}`}
                </p>
                <span className={`text-[11px] font-mono px-2 py-0.5 rounded uppercase tracking-wider ${
                  a?.phase === "forced" ? "bg-slate-700 text-slate-300"
                    : deciderIsYou ? "bg-amber-500/20 text-amber-300" : "bg-cyan-500/20 text-cyan-300"
                }`}>
                  {a?.phase === "forced" ? "Auto" : deciderIsYou ? "Your decision" : "Opponent's decision"}
                </span>
              </div>

              {pendingPlayer && a?.phase !== "forced" && (
                <div className="mb-4 rounded-lg border border-slate-700 bg-slate-900/50 px-4 py-2.5 flex items-center gap-3">
                  <Flag className="h-4 w-4 text-amber-400" />
                  <img src={factionIcon(pendingPlayer.faction)} alt="" className="h-6 w-6 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">On the table</p>
                    <p className="text-sm font-semibold">{pendingPlayer.name} <span className="text-slate-500 font-mono text-xs">· {pendingPlayer.faction}</span></p>
                  </div>
                </div>
              )}

              <p className="text-sm text-slate-300 mb-4">{instruction()}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {a?.options?.map((opt, i) => (
                  <OptionButton key={(opt.ids || []).join("-") || i} opt={opt} phase={a.phase} deciderIsYou={deciderIsYou}
                    pending={pendingPlayer} setup={setup} nameOf={nameOf} onPick={() => pick(opt)} />
                ))}
              </div>
            </Card>
          )}

          {/* locked matchups with table inputs */}
          {lockedCount > 0 && (
            <Card className="bg-[#121824]/70 border-slate-800 p-5">
              <h3 className="font-display font-bold uppercase tracking-wide text-sm mb-3">Locked Matchups</h3>
              <div className="space-y-2">
                {a.pairings.map((p, i) => {
                  const yourId = setup.you_are === "A" ? p.a : p.b;
                  const oppId = setup.you_are === "A" ? p.b : p.a;
                  const cell = effectiveCell(setup, yourId, oppId, nameOf(oppId).faction);
                  const v = cellValue(cell);
                  const st = tierStyle(cell);
                  return (
                    <div key={`${p.a}|${p.b}`} className="flex items-center gap-2 rounded-lg border px-3 py-2"
                      style={{ background: `${st.bg}80`, borderColor: st.border }}>
                      <span className="text-xs font-mono text-slate-500 w-5">{i + 1}</span>
                      <div className="flex-1 flex items-center gap-1.5 text-sm min-w-0">
                        <img src={factionIcon(p.a_player.faction)} alt="" className="h-4 w-4 object-contain shrink-0" onError={(e) => (e.currentTarget.style.display = "none")} />
                        <span className="text-amber-200 font-semibold truncate">{p.a_player.name}</span>
                        <span className="text-slate-500 text-xs">vs</span>
                        <span className="text-cyan-200 font-semibold truncate">{p.b_player.name}</span>
                        <img src={factionIcon(p.b_player.faction)} alt="" className="h-4 w-4 object-contain shrink-0" onError={(e) => (e.currentTarget.style.display = "none")} />
                      </div>
                      {cell?.tier && (
                        <span className="font-mono text-xs font-bold px-1.5 py-0.5 rounded" style={{ color: st.text }}>
                          {cellLabel(cell)}
                        </span>
                      )}
                      <Input value={tables[`${p.a}|${p.b}`] || ""}
                        onChange={(e) => setTable(`${p.a}|${p.b}`, e.target.value)}
                        placeholder="Table" className="w-24 h-8 bg-slate-950/60 border-slate-800 text-xs" data-testid="table-input" />
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>

        {/* projection sidebar */}
        <div className="lg:col-span-2">
          <Card className="bg-[#121824]/70 border-cyan-700/30 p-5 sticky top-32">
            <div className="flex items-center gap-2 mb-1">
              <Target className="h-4 w-4 text-cyan-400" />
              <h3 className="font-display font-bold uppercase tracking-wide text-sm">Projection from here</h3>
            </div>
            {a?.projection && (
              <>
                <div className="flex items-baseline gap-3 mb-3">
                  <p className="font-mono text-3xl font-bold">
                    <span className="text-emerald-400">{a.projection.wins}</span>
                    <span className="text-slate-600">–</span>
                    <span className="text-red-400">{a.projection.losses}</span>
                  </p>
                  <p className={`font-mono text-lg font-bold ${a.projection.score >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {fmtScore(a.projection.score)}
                  </p>
                </div>
                <ProjectionList proj={a.projection} setup={setup} lockedCount={lockedCount} nameOf={nameOf} />
                <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                  Future matchups assume optimal adversarial play. Locked matchups are settled.
                </p>
              </>
            )}
          </Card>
        </div>
      </div>

      {/* live pairing matrix with predicted route */}
      <Card className="bg-[#121824]/70 border-slate-800 p-5">
        <h3 className="font-display font-bold uppercase tracking-wide text-sm mb-3 flex items-center gap-2">
          <Target className="h-4 w-4 text-cyan-400" /> Pairing Matrix &amp; Predicted Route
        </h3>
        <ResolverMatrix setup={setup} analysis={a} />
      </Card>

      {a?.sequence?.length > 1 && (
        <Card className="bg-[#121824]/70 border-slate-800 p-4" data-testid="live-sequence-card">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Pairing sequence — locked so far &amp; projected from here</p>
          <PairingSequence sequence={a.sequence} youAre={setup.you_are} teamAName={setup.team_a_name} teamBName={setup.team_b_name} />
        </Card>
      )}

      {!a?.complete && <WinRoutes analysis={a} setup={setup} />}

      <OpponentLists setup={setup} updateSetup={updateSetup} />
    </div>
  );
}

function OpponentLists({ setup, updateSetup }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const opp = setup.players.filter((p) => p.team !== setup.you_are);
  const hasText = opp.some((p) => (p.lists || []).some((l) => (l.text || "").trim()));
  const canFetch = opp.some((p) => p.ls_id && p.ls_event_id);

  const fetchLists = async () => {
    setBusy(true);
    let ok = 0;
    const byId = {};
    for (const p of opp.filter((x) => x.ls_id && x.ls_event_id)) {
      try {
        const res = await api.fetchList(p.ls_id, p.ls_event_id);
        if (res.status === "OK" && res.lists.length) {
          byId[p.id] = res.lists.map((l) => ({ name: l.name, army: p.faction, text: l.text }));
          ok += 1;
        }
      } catch (e) { console.debug("opponent list fetch failed", e); }
    }
    updateSetup({ ...setup, players: setup.players.map((p) => (byId[p.id] ? { ...p, lists: byId[p.id] } : p)) });
    setBusy(false);
    toast[ok ? "success" : "error"](ok ? `Fetched lists for ${ok} opponent(s)` : "No lists found");
  };

  return (
    <Card className="bg-[#121824]/70 border-slate-800 p-5" data-testid="opponent-lists-card">
      <div className="flex items-center justify-between gap-2">
        <button onClick={() => setOpen((v) => !v)} className="flex-1 flex items-center justify-between gap-2">
          <h3 className="font-display font-bold uppercase tracking-wide text-sm flex items-center gap-2">
            <FileText className="h-4 w-4 text-cyan-400" /> Opponent Lists
            {!hasText && !canFetch && <span className="text-[11px] text-slate-500 font-normal normal-case">— add list text in Teams &amp; Import</span>}
          </h3>
          {open ? <ChevronRight className="h-4 w-4 text-slate-500 rotate-90" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
        </button>
        {canFetch && (
          <Button data-testid="resolver-fetch-lists-button" onClick={fetchLists} disabled={busy} size="sm" variant="outline"
            className="h-7 border-cyan-700/50 bg-cyan-950/30 text-cyan-300 hover:bg-cyan-900/40 text-xs shrink-0">
            {busy ? "Fetching…" : "Fetch from Longshanks"}
          </Button>
        )}
      </div>
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {opp.map((p) => {
            const count = (p.lists || []).filter((l) => (l.text || "").trim()).length;
            return (
              <ListDialog key={p.id} player={p}>
                <button data-testid="opp-list-card" className="text-left rounded-lg border border-slate-800 bg-slate-900/40 p-3 hover:border-cyan-600/50 hover:bg-slate-900/70 transition-colors">
                  <div className="flex items-center gap-2">
                    <img src={factionIcon(p.faction)} alt="" className="h-6 w-6 object-contain shrink-0" onError={(e) => (e.currentTarget.style.display = "none")} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-cyan-300 truncate">{p.name}</p>
                      <p className="text-[11px] text-slate-500 font-mono truncate">{p.faction}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {count > 0 ? (
                      (p.lists || []).filter((l) => (l.text || "").trim()).map((l, i) => (
                        <span key={`${l.name || "list"}-${i}`} className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-600/30 rounded px-1.5 py-0.5 truncate max-w-full">{l.name || `List ${i + 1}`}</span>
                      ))
                    ) : (
                      <span className="text-[11px] text-slate-600 italic">No list — click to view / fetch</span>
                    )}
                  </div>
                  <p className="text-[10px] text-cyan-500/70 mt-2 flex items-center gap-1"><FileText className="h-3 w-3" /> Click to read full list</p>
                </button>
              </ListDialog>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function OptionButton({ opt, phase, deciderIsYou, pending, setup, nameOf, onPick }) {
  const rec = opt.recommended;
  const testid =
    phase === "first_pick" ? "resolver-nominate-player-btn"
      : phase === "nominate" ? "resolver-offer-player-btn"
      : phase === "forced" ? "resolver-step-next-button"
      : "resolver-select-pairing-btn";

  const players = opt.players || [];
  const st = valueStyle(objWinDelta(opt.value));

  // When a single matchup is formed (choose / forced), show its tier designation
  // instead of the aggregate net-wins number.
  const matchupCell = (() => {
    if (!opt.matchup || !setup || !nameOf) return null;
    const { a: aId, b: bId } = opt.matchup;
    const yourId = setup.you_are === "A" ? aId : bId;
    const oppId = setup.you_are === "A" ? bId : aId;
    return effectiveCell(setup, yourId, oppId, nameOf(oppId).faction);
  })();

  return (
    <button
      data-testid={testid}
      onClick={onPick}
      className={`relative text-left rounded-lg border px-3.5 py-3 transition-transform hover:-translate-y-0.5 ${rec ? "recommend-glow" : ""}`}
      style={{ background: `${st.bg}cc`, borderColor: rec ? "#22d3ee" : st.border }}
    >
      {rec && (
        <span className="absolute -top-2 right-2 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-cyan-500 text-slate-900 uppercase">
          {deciderIsYou ? "Optimal" : "Predicted"}
        </span>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {players.map((p, i) => (
              <span key={`${p.name}-${i}`} className="flex items-center gap-1 min-w-0">
                {i > 0 && <span className="text-slate-400 text-xs px-0.5">+</span>}
                <img src={factionIcon(p.faction)} alt="" className="h-4 w-4 object-contain shrink-0" onError={(e) => (e.currentTarget.style.display = "none")} />
                <span className="text-sm font-semibold truncate" style={{ color: st.text }}>{p.name}</span>
              </span>
            ))}
          </div>
          <p className="text-[11px] font-mono truncate mt-0.5" style={{ color: `${st.text}cc` }}>
            {phase === "nominate" ? players.map((p) => p.faction).join(" · ") : players[0]?.faction || ""}
          </p>
          {opt.matchup_named && (
            <p className="text-[11px] mt-0.5 flex items-center gap-1" style={{ color: `${st.text}cc` }}>
              forms <img src={factionIcon(opt.matchup_named.a.faction)} alt="" className="h-3.5 w-3.5 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />
              <span className="text-amber-200">{opt.matchup_named.a.name}</span>
              <span className="opacity-60">vs</span>
              <img src={factionIcon(opt.matchup_named.b.faction)} alt="" className="h-3.5 w-3.5 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />
              <span className="text-cyan-200">{opt.matchup_named.b.name}</span>
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          {matchupCell ? (
            <>
              <p className="font-mono text-sm font-bold" style={{ color: st.text }} data-testid="option-tier">{cellLabel(matchupCell)}</p>
              <p className="text-[9px] uppercase" style={{ color: `${st.text}99` }}>matchup</p>
            </>
          ) : (
            <>
              <p className="font-mono text-sm font-bold" style={{ color: st.text }}>{fmtScore(objWinDelta(opt.value))}</p>
              <p className="text-[9px] uppercase" style={{ color: `${st.text}99` }}>net wins</p>
            </>
          )}
        </div>
      </div>
      {phase === "forced" && (
        <div className="mt-1 text-xs flex items-center gap-1 font-semibold" style={{ color: st.text }}>
          Confirm <ArrowRight className="h-3 w-3" />
        </div>
      )}
    </button>
  );
}

function ProjectionList({ proj, setup, lockedCount, nameOf }) {
  const you = setup.you_are;
  return (
    <div className="space-y-1.5">
      {proj.pairings.map((p, i) => {
        const locked = i < lockedCount;
        const yourId = you === "A" ? p.a : p.b;
        const oppId = you === "A" ? p.b : p.a;
        const cell = effectiveCell(setup, yourId, oppId, nameOf(oppId).faction);
        const st = tierStyle(cell);
        const aFac = p.a_player?.faction;
        const bFac = p.b_player?.faction;
        return (
          <div key={`${p.a}|${p.b}`} data-testid="projection-row">
            <div className="flex items-center gap-2 rounded px-2.5 py-2 text-sm border"
              style={{ background: `${st.bg}99`, borderColor: st.border }}>
              {locked ? <Check className="h-3.5 w-3.5 text-amber-300 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
              <img src={factionIcon(aFac)} alt="" className="h-4 w-4 object-contain shrink-0" onError={(e) => (e.currentTarget.style.display = "none")} />
              <span className="text-amber-200 truncate flex-1 font-medium">{p.a_player?.name}</span>
              <span className="text-slate-400 text-xs">vs</span>
              <span className="text-cyan-200 truncate flex-1 text-right font-medium">{p.b_player?.name}</span>
              <img src={factionIcon(bFac)} alt="" className="h-4 w-4 object-contain shrink-0" onError={(e) => (e.currentTarget.style.display = "none")} />
              <span className="font-mono text-xs font-bold w-12 text-right" style={{ color: st.text }} data-testid="projection-tier">{cellLabel(cell) || fmtScore(p.value)}</span>
            </div>
            {cell?.note && (
              <p className="text-[11px] text-amber-300/90 flex items-start gap-1 px-2.5 py-1 mt-0.5 bg-amber-500/5 rounded" data-testid="projection-note">
                <StickyNote className="h-3 w-3 mt-0.5 shrink-0" /> {cell.note}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ReadinessPanel({ setup }) {
  const yours = setup.players.filter((p) => p.team === setup.you_are);
  const opp = setup.players.filter((p) => p.team !== setup.you_are);
  if (yours.length === 0 || opp.length === 0) return null;
  const rows = yours.map((y) => {
    const rated = opp.filter((o) => effectiveCell(setup, y.id, o.id, o.faction).source !== "fallback").length;
    return { y, rated, ready: rated === opp.length };
  });
  const readyCount = rows.filter((r) => r.ready).length;
  const allReady = readyCount === yours.length;
  return (
    <Card className={`p-4 ${allReady ? "bg-emerald-950/20 border-emerald-600/40" : "bg-[#121824]/70 border-amber-600/30"}`} data-testid="resolver-readiness-panel">
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 className={`h-4 w-4 ${allReady ? "text-emerald-400" : "text-amber-400"}`} />
        <h3 className="font-display font-bold uppercase tracking-wide text-sm">Team Readiness</h3>
        <span className={`text-xs font-mono ${allReady ? "text-emerald-300" : "text-amber-300"}`} data-testid="resolver-readiness-summary">
          {allReady ? "Grid complete" : `${readyCount}/${yours.length} rated`}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {rows.map((r) => (
          <span key={r.y.id} data-testid="resolver-readiness-row"
            className={`text-xs rounded px-2 py-1 flex items-center gap-1.5 border ${r.ready ? "border-emerald-600/40 bg-emerald-500/10 text-emerald-300" : "border-slate-700 bg-slate-900/40 text-slate-300"}`}>
            {r.ready
              ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" data-testid="resolver-player-ready-tick" />
              : <span className="text-[10px] font-mono text-amber-400/90">{r.rated}/{opp.length}</span>}
            {r.y.name || "—"}
          </span>
        ))}
      </div>
    </Card>
  );
}

function RoundHeader({ setup, onNextRound }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono uppercase tracking-widest text-cyan-400/80">{setup.event_name || "No event"}</span>
        <span className="text-sm font-display font-black uppercase tracking-tight text-amber-400">{setup.round_name || `Round ${setup.round_number || 1}`}</span>
      </div>
      <Button data-testid="prestart-next-round-button" onClick={onNextRound} variant="outline" size="sm"
        className="border-cyan-700/50 bg-cyan-950/20 text-cyan-300 hover:bg-cyan-900/40">
        <Repeat className="h-3.5 w-3.5 mr-1.5" /> Next round
      </Button>
    </div>
  );
}


function Segmented({ value, onChange, options, testid }) {
  return (
    <div className="flex rounded-lg border border-slate-700 overflow-hidden">
      {options.map((o) => (
        <button
          key={o.v}
          data-testid={`${testid}-${o.v}`}
          onClick={() => onChange(o.v)}
          className={`flex-1 px-3 py-2 text-sm font-semibold truncate transition-colors ${
            value === o.v ? "bg-amber-500 text-slate-900" : "bg-transparent text-slate-400 hover:text-slate-200"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
